import type { ConnectorSurface, KnowledgeBase } from '@ralph/contracts'
import { SURFACE_LABEL } from '@ralph/contracts'
import { ShieldAlert, TriangleAlert } from 'lucide-react'
import { useId } from 'react'
import { useViewer } from '@/hooks/use-connectors.ts'
import { useBaseAccess } from '@/hooks/use-knowledge-bases.ts'
import { useI18n } from '@/i18n/index.ts'
import { folderReaders } from './connected-folders.ts'

/**
 * The access boundary, said out loud, immediately above Start (PRD-25 D-8, PRD-26 D-38).
 *
 * Everything a folder loads is searchable by whoever can use the knowledge base — whoever can
 * open the file at the provider. The block names them the way the base's own "Who can use it"
 * does (you, a group's managers, the groups it is shared with and how many people that is, or
 * everyone who signs in), dismisses the provider's permission model explicitly, counts the agents
 * allowed to answer from the base for everyone they reach, and offers the way out.
 *
 * Its strength scales with the audience: a base that is private or shared with groups gets a
 * plain sentence in a bordered box — a fact, not a warning, and painting it red for the common,
 * correct case teaches people to dismiss it — while a base shared with everyone gets the alert
 * treatment and a checkbox that gates the button. A checkbox on every path is a checkbox nobody
 * reads; one on the path that can leak a whole HR drive is one somebody pauses at.
 */
export const FolderAudienceBlock = ({
  base,
  surface,
  acknowledged,
  onAcknowledge,
  stale
}: {
  base: Pick<KnowledgeBase, 'id' | 'name' | 'access' | 'owner' | 'lentToCount'>
  surface: ConnectorSurface
  acknowledged: boolean
  onAcknowledge: (value: boolean) => void
  /** Who can reach the base widened after an earlier acknowledgement. */
  stale?: boolean
}) => {
  const viewer = useViewer()
  // The words and the formatters both come from the body, never from a module-scope capture
  // that would freeze the language at import (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  // The distinct number of people, from the same view the base's "Who can use it" reads.
  const access = useBaseAccess(base.id, base.access.kind === 'groups')
  const checkboxId = useId()
  const readers = folderReaders(fmt, base, viewer.id, access.data?.reach ?? null)
  const surfaceLabel = SURFACE_LABEL[surface]

  if (readers.kind !== 'everyone') {
    return (
      <div className="rounded-tile border border-rule-strong bg-card-solid/55 px-3.5 py-3">
        <p className="flex items-start gap-2 text-[13px] leading-[1.5]">
          <ShieldAlert size={14} strokeWidth={2.2} className="mt-[3px] shrink-0 text-fg-muted" aria-hidden />
          <span className="min-w-0">
            <span className="block font-semibold">{t.access.headline(readers, base.name)}</span>
            {readers.kind === 'groups' && (
              <span className="mt-1 block text-fg-muted">{t.access.groupsAre(fmt, readers.names, readers.people)}</span>
            )}
            <span className="mt-1 block text-fg-muted">{t.access.provider(surfaceLabel, base.name)}</span>
            {base.lentToCount > 0 && (
              <span className="mt-1 block text-fg-muted">{t.access.agents(fmt, base.lentToCount, base.name)}</span>
            )}
            <span className="mt-1 block text-fg-subtle">
              {readers.kind === 'private' ? t.access.privateLater(base.name) : t.access.restrictedWayOut}
            </span>
            {stale && <span className="mt-1.5 block text-alert">{t.access.widened(base.name)}</span>}
          </span>
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-tile border border-alert/40 bg-alert-soft px-3.5 py-3">
      <p className="flex items-start gap-2 text-[13px] leading-[1.5]">
        <TriangleAlert size={14} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
        <span className="min-w-0">
          <span className="block font-semibold">{t.access.headline(readers, base.name)}</span>
          <span className="mt-1 block text-fg-muted">{t.access.everyoneBody(base.name, surfaceLabel)}</span>
          {stale && <span className="mt-1.5 block text-alert">{t.access.widened(base.name)}</span>}
        </span>
      </p>
      <label htmlFor={checkboxId} className="mt-2.5 flex cursor-pointer items-start gap-2 text-[13px] leading-[1.45]">
        <input
          id={checkboxId}
          type="checkbox"
          checked={acknowledged}
          onChange={(event) => onAcknowledge(event.target.checked)}
          className="mt-[3px] h-[15px] w-[15px] shrink-0 cursor-pointer accent-primary"
        />
        <span>{t.access.everyoneAck}</span>
      </label>
      <p className="mt-2 text-[12.5px] leading-[1.45] text-fg-subtle">{t.access.everyoneWayOut(base.name)}</p>
    </div>
  )
}
